import { workbenchDocumentsApi } from "../../../api/client";

/**
 * Category sent to the BFF for proofs uploaded from the Send Proofs modal.
 * Mirrors proof-portal-ui's dealer upload flow, which files these documents
 * under "OTHER".
 */
const PROOF_CATEGORY = "OTHER";

export interface UploadProofParameters {
  /** Credit application id (fed the route `approvalId`, matching sibling reads). */
  appId: string;
  /** Dealer id from the dashboard deal summary; omitted when unavailable. */
  dealerId?: string;
  /** The file to upload. */
  file: File;
  /** Optional abort signal, propagated to both the BFF and S3 requests. */
  signal?: AbortSignal;
}

/**
 * Uploads a single proof file in two steps:
 *  1. Ask the BFF for a presigned S3 URL (`generateUploadUrl`).
 *  2. PUT the raw file bytes directly to that URL.
 *
 * The S3 PUT intentionally uses a bare `fetch` (no cookies / auth headers): the
 * presigned URL carries its own signature, and sending the session credentials
 * cross-origin to S3 would break the request. Both steps share the abort signal.
 * @param {UploadProofParameters} parameters - appId, dealerId, file, and optional signal.
 * @returns {Promise<string>} The proofFileId assigned by the BFF.
 */
export async function uploadProofFile({ appId, dealerId, file, signal }: UploadProofParameters): Promise<string> {
  const { data } = await workbenchDocumentsApi.generateUploadUrl(
    appId,
    { category: PROOF_CATEGORY, filename: file.name, dealerId },
    { signal }
  );

  if (!data.uploadUrl) {
    throw new Error("Upload URL was not returned by the server.");
  }

  const response = await fetch(data.uploadUrl, {
    method: "PUT",
    body: file,
    // Match the object's content type so S3 stores it correctly; omit the
    // header entirely when the browser couldn't infer a type.
    headers: file.type ? { "Content-Type": file.type } : undefined,
    signal,
  });

  if (!response.ok) {
    throw new Error(`Upload failed (HTTP ${response.status}).`);
  }

  return data.proofFileId ?? "";
}
